import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, PhoneOutgoing, Upload, Paperclip, Trash2, RefreshCw, PhoneIncoming, Eye } from 'lucide-react';
import {
    fetchTestDialerSetup, deleteTestDialerSetup,
} from '../../../api/dialerTest.js';
import { fetchMembers, deleteMember } from '../../../api/memberDirectory.js';

/**
 * DialerListModal — 工作室「撥號名單」按鈕
 *
 * 三 tab：
 *   進線匯入：上傳名單 → sendMessage 觸發 import-member-list skill → 寫 avatar.members
 *   外播匯入：上傳名單 + 流程文件 → sendMessage 觸發 import-outbound-list + sync-ivr-config
 *               → 寫 avatar.testDialerSetup + sync flow config 對應節點
 *   檢視名單：兩種來源並列在同個列表
 *
 * 整體推理過程透過 onApply(prompt, { files, displayText }) push 進工作室聊天室，
 * Claude 在 build session 內跑 skill，所有推理 stream 到 chat（不在 modal 內 polling）。
 */
export function DialerListModal({ open, onClose, onApply, isStreaming, avatarId, avatarName }) {
    const { t } = useTranslation('wsTest');
    const [tab, setTab] = useState('inbound-import');

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-4xl bg-white rounded-md shadow-2xl border border-cream-200 max-h-[90vh] flex flex-col">
                <div className="px-5 py-3 border-b border-cream-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <PhoneOutgoing size={16} className="text-accent-700" strokeWidth={1.6} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-500">Dialer List</span>
                        <span className="heading-display text-base font-semibold text-ink-900 ml-1">{t('dialer.title')}</span>
                        {avatarName && (
                            <span className="font-mono text-[10px] text-ink-400 ml-2">{avatarName}</span>
                        )}
                    </div>
                    <button onClick={onClose} className="size-7 inline-flex items-center justify-center rounded text-ink-400 hover:bg-cream-100 transition">
                        <X size={14} strokeWidth={2} />
                    </button>
                </div>

                <div className="px-5 pt-3 border-b border-cream-200 flex gap-0.5">
                    {[
                        { key: 'inbound-import', label: t('dialer.tab.inboundImport'), icon: PhoneIncoming },
                        { key: 'outbound-import', label: t('dialer.tab.outboundImport'), icon: PhoneOutgoing },
                        { key: 'view', label: t('dialer.tab.view'), icon: Eye },
                    ].map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={[
                                'px-3 py-2 text-xs font-medium inline-flex items-center gap-1.5 border-b-2 transition -mb-px',
                                tab === key
                                    ? 'border-accent-500 text-accent-700'
                                    : 'border-transparent text-ink-500 hover:text-ink-900',
                            ].join(' ')}
                        >
                            <Icon size={13} strokeWidth={1.8} />
                            {label}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {tab === 'inbound-import' && (
                        <ImportPanel
                            mode="inbound"
                            avatarId={avatarId}
                            avatarName={avatarName}
                            isStreaming={isStreaming}
                            onApply={onApply}
                            afterSubmit={onClose}
                        />
                    )}
                    {tab === 'outbound-import' && (
                        <ImportPanel
                            mode="outbound"
                            avatarId={avatarId}
                            avatarName={avatarName}
                            isStreaming={isStreaming}
                            onApply={onApply}
                            afterSubmit={onClose}
                        />
                    )}
                    {tab === 'view' && <ViewPanel avatarId={avatarId} />}
                </div>
            </div>
        </div>
    );
}

// ─── Import Panel ────────────────────────────────────────────────────────
function ImportPanel({ mode, avatarId, avatarName, isStreaming, onApply, afterSubmit }) {
    const { t } = useTranslation('wsTest');
    const [file, setFile] = useState(null);
    const [flowDoc, setFlowDoc] = useState(null);
    const [importMode, setImportMode] = useState('append');
    const [instructions, setInstructions] = useState('');

    const isOutbound = mode === 'outbound';
    const description = isOutbound
        ? t('dialer.import.descOutbound')
        : t('dialer.import.descInbound');

    const canApply = !!file && !isStreaming;

    function submit() {
        if (!canApply) return;
        const files = [file, flowDoc].filter(Boolean);
        const prompt = buildPrompt({ mode, avatarId, avatarName, file, flowDoc, importMode, instructions });
        const modeLabel = importMode === 'append' ? t('dialer.import.modeAppendShort') : t('dialer.import.modeOverwriteShort');
        const flowDocSuffix = flowDoc ? t('dialer.import.flowDocSuffix') : '';
        const instructionsSuffix = instructions ? t('dialer.import.instructionsSuffix') : '';
        const displayText = isOutbound
            ? t('dialer.import.displayOutbound', { flowDoc: flowDocSuffix, mode: modeLabel, instructions: instructionsSuffix })
            : t('dialer.import.displayInbound', { mode: modeLabel, instructions: instructionsSuffix });
        onApply(prompt, { files, displayText });
        // chat 顯示推理過程；關閉 modal 讓用戶看 chat
        afterSubmit?.();
    }

    return (
        <div className="space-y-4">
            <div className="text-xs text-ink-500 leading-relaxed bg-cream-50 border border-cream-200 rounded p-3">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mr-1">
                    {isOutbound ? 'OUTBOUND' : 'INBOUND'}
                </span>
                {description}
            </div>

            <div>
                <label className="text-xs font-medium text-ink-700 block mb-1">{t('dialer.import.fileLabel')}</label>
                <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.tsv,.txt,.docx,.pdf,.png,.jpg,.jpeg,.heic"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-cream-300 file:bg-white file:text-ink-700 file:text-xs hover:file:bg-cream-50"
                />
                {file && <div className="mt-1 text-[11px] text-ink-500"><Paperclip size={11} className="inline" /> {file.name} ({(file.size/1024).toFixed(1)} KB)</div>}
            </div>

            {isOutbound && (
                <div>
                    <label className="text-xs font-medium text-ink-700 block mb-1">{t('dialer.import.flowDocLabel')}</label>
                    <input
                        type="file"
                        accept=".docx,.pdf,.md,.txt"
                        onChange={(e) => setFlowDoc(e.target.files?.[0] || null)}
                        className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-cream-300 file:bg-white file:text-ink-700 file:text-xs hover:file:bg-cream-50"
                    />
                    {flowDoc && <div className="mt-1 text-[11px] text-ink-500"><Paperclip size={11} className="inline" /> {flowDoc.name}</div>}
                    <div className="mt-1 text-[10px] text-ink-400">
                        {t('dialer.import.flowDocHint')}
                    </div>
                </div>
            )}

            <div>
                <label className="text-xs font-medium text-ink-700 block mb-1">{t('dialer.import.instructionsLabel')}</label>
                <textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={3}
                    placeholder={isOutbound
                        ? t('dialer.import.instructionsPlaceholderOutbound')
                        : t('dialer.import.instructionsPlaceholderInbound')}
                    className="w-full px-2 py-1.5 text-xs border border-cream-300 rounded resize-none focus:outline-none focus:border-accent-400 leading-relaxed"
                />
                <div className="mt-0.5 text-[10px] text-ink-400">
                    {t('dialer.import.instructionsHint')}
                </div>
            </div>

            <div>
                <label className="text-xs font-medium text-ink-700 block mb-1">{t('dialer.import.importModeLabel')}</label>
                <div className="flex gap-3">
                    {['append', 'overwrite'].map((m) => (
                        <label key={m} className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" checked={importMode === m} onChange={() => setImportMode(m)} className="accent-accent-500" />
                            {m === 'append' ? t('dialer.import.modeAppend') : t('dialer.import.modeOverwrite')}
                        </label>
                    ))}
                </div>
            </div>

            <div className="flex items-center justify-between pt-2">
                <div className="text-[10px] text-ink-400">
                    {t('dialer.import.submitHint')}
                </div>
                <button
                    disabled={!canApply}
                    onClick={submit}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-accent-500 hover:bg-accent-700 text-cream-50 text-xs font-medium disabled:bg-cream-200 disabled:text-ink-400 disabled:cursor-not-allowed"
                >
                    <Upload size={13} /> {t('dialer.import.submit')}
                </button>
            </div>
        </div>
    );
}

// ─── Build Claude prompt (sent to ChatPane via onApply) ─────────────────
function buildPrompt({ mode, avatarId, avatarName, file, flowDoc, importMode, instructions }) {
    const isOutbound = mode === 'outbound';
    const fileLines = [file, flowDoc].filter(Boolean).map((f, i) => `  ${i + 1}. ${f.name} (${Math.round(f.size/1024)} KB)`).join('\n');
    const lines = [];

    if (isOutbound) {
        lines.push(
            `請用 \`import-outbound-list\` skill 把以下名單匯入這個客服（${avatarName || 'avatar'}）作為外播測試用。`,
            ``,
            `AVATAR_ID: ${avatarId}`,
            `IMPORT_MODE: ${importMode}`,
            ``,
            `【附上的檔案】`,
            fileLines,
            `（路徑由 update endpoint 自動帶入。xlsx 用 node-xlsx；docx 用 unzip + strip XML；pdf 用 pdftotext。）`,
            ``,
        );

        if (flowDoc) {
            lines.push(
                `## 走 import-outbound-list skill Mode B（整套外播流程匯入到客服）`,
                ``,
                `⚠️ **流程圖內文字 verbatim 照抄** — Announce / Reminder / 對話分支 box 等流程圖內所有文字必須**逐字**寫進 greetingTemplate / agentInstruction，**不要改字、不要優化、不要重組**。違反這條會破壞整個流程設計。詳見 SKILL.md「🚨 內容 verbatim 抽取規則」段落。`,
                ``,
                `因為 \`AVATAR_ID\` 有給 + 同時附流程文件，請依 \`import-outbound-list\` SKILL.md「Mode B: 整套外播流程匯入到 avatar」章節整套跑：`,
                ``,
                `1. 解析名單 + 流程文件（Step 1-5 of skill）`,
                `2. 編排其他 skill（B-1 ~ B-5）：`,
                `   - \`sync-ivr-config\` read-config 看 \`${avatarId}\` 現有 nodes`,
                `   - 沒合適 \`formCollection\` 節點 → \`add-form-collection\` 建（欄位 schema 從文件抽）`,
                `   - \`sync-ivr-config\` 寫對話設計 + greetingTemplates`,
                `   - \`refine-agent-prompt\` 加 few-shot`,
                `   - PUT /api/avatar/${avatarId}/test-dialer-setup（**不可省略**）`,
                `3. 商家口吻摘要回報`,
                ``,
                `細節規則 / 萬用原則全部依 SKILL.md，不要在 chat 內推敲。`,
            );
        } else {
            lines.push(
                `## 任務（只解析名單，無流程文件）`,
                `1. 用 import-outbound-list skill 智能辨識欄位 → 產出 contacts。`,
                `2. PUT /api/avatar/${avatarId}/test-dialer-setup 帶 { testDialerSetup: { contacts } }`,
                `3. 商家口吻摘要結果。`,
            );
        }
    } else {
        // inbound — 沿用既有 MemberDirectoryModal 的流程
        lines.push(
            `請用 \`import-member-list\` skill 把以下名單匯入這個客服（${avatarName || 'avatar'}）的會員資料庫。`,
            ``,
            `AVATAR_ID: ${avatarId}`,
            `MODE: ${importMode}`,
            ``,
            `【附上的名單檔案】`,
            fileLines,
            `（路徑由 update endpoint 自動帶入。）`,
            ``,
            `## 執行步驟`,
            `1. 讀名單檔案，動態辨識電話、身分證、會員編號、Email 等 identity 欄位 + 姓名 / 地址 / 飲食 / 偏好醫療 / 行動輔具等 profile 欄位`,
            `2. Normalize 電話成主鍵（去 dash / 空白）；沒電話的 row 跳過`,
            `3. 性別欄推 title：男→先生 / 女→小姐 / 其他→您`,
            `4. 呼叫 scripts/write_members.js 寫進 Firestore subcollection + invalidate Redis + 更新 avatar.memberDirectoryFields`,
            `5. 用商家口吻摘要結果。⚠️ 不要貼 JSON、不要解釋技術細節`,
        );
    }

    if (instructions.trim()) {
        lines.push(
            ``,
            `## ⭐ 用戶補充指示（最高優先）`,
            `> ${instructions.trim().split('\n').join('\n> ')}`,
        );
    }

    return lines.join('\n');
}

// ─── View Panel — 兩種來源並列 ────────────────────────────────────────────
function ViewPanel({ avatarId }) {
    const { t } = useTranslation('wsTest');
    const [members, setMembers] = useState([]);
    const [setup, setSetup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    async function reload() {
        setLoading(true);
        const [m, s] = await Promise.all([
            fetchMembers(avatarId).catch(() => []),
            fetchTestDialerSetup(avatarId).catch(() => null),
        ]);
        setMembers(m || []);
        setSetup(s || null);
        setLoading(false);
    }
    useEffect(() => { reload(); }, [avatarId]);

    const rows = [];
    if (filter === 'all' || filter === 'inbound') {
        for (const m of members) rows.push({ source: 'inbound', ...m });
    }
    if (filter === 'all' || filter === 'outbound') {
        for (const c of (setup?.contacts || [])) {
            const v = c.variables || {};
            rows.push({
                source: 'outbound',
                phone: c.phones?.[0] || '-',
                name: v.name || '-',
                title: v.title || '',
                category: v.category || '',
                _contact: c,
            });
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-500">Filter</span>
                {[
                    { key: 'all', label: t('common:state.all') },
                    { key: 'inbound', label: t('dialer.view.filterInbound') },
                    { key: 'outbound', label: t('dialer.view.filterOutbound') },
                ].map(({ key, label }) => (
                    <button
                        key={key}
                        onClick={() => setFilter(key)}
                        className={[
                            'text-xs px-2 py-1 rounded transition',
                            filter === key ? 'bg-accent-500 text-cream-50' : 'bg-cream-100 text-ink-700 hover:bg-cream-200',
                        ].join(' ')}
                    >
                        {label}
                    </button>
                ))}
                <button onClick={reload} className="ml-auto text-xs px-2 py-1 rounded text-ink-500 hover:bg-cream-100">
                    <RefreshCw size={11} className="inline" />
                </button>
            </div>

            {setup && (filter === 'all' || filter === 'outbound') && (
                <div className="text-[11px] text-ink-500 bg-cream-50 border border-cream-200 rounded p-2 flex items-center justify-between">
                    <span>
                        {t('dialer.view.setupContacts', { n: setup.contacts?.length || 0 })}
                        {setup.greetingTemplates && <span> · {t('dialer.view.setupTemplates', { n: Object.keys(setup.greetingTemplates).length })}</span>}
                        {setup.flowDoc?.filename && <span> · {t('dialer.view.setupFlowDoc', { name: setup.flowDoc.filename })}</span>}
                    </span>
                    <button
                        onClick={async () => {
                            if (!confirm(t('dialer.view.deleteSetupConfirm'))) return;
                            await deleteTestDialerSetup(avatarId);
                            reload();
                        }}
                        className="text-danger-500 hover:underline"
                    >
                        {t('dialer.view.clearSetup')}
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-xs text-ink-400 italic py-8 text-center">{t('common:state.loading')}</div>
            ) : rows.length === 0 ? (
                <div className="text-xs text-ink-400 italic py-8 text-center">{t('dialer.view.empty')}</div>
            ) : (
                <div className="border border-cream-200 rounded overflow-hidden">
                    <table className="w-full text-xs">
                        <thead className="bg-cream-50 text-ink-500">
                            <tr>
                                <th className="px-2 py-1.5 text-left font-mono text-[10px] uppercase tracking-wider">{t('dialer.view.colSource')}</th>
                                <th className="px-2 py-1.5 text-left">{t('dialer.view.colPhone')}</th>
                                <th className="px-2 py-1.5 text-left">{t('dialer.view.colName')}</th>
                                <th className="px-2 py-1.5 text-left">{t('dialer.view.colCategory')}</th>
                                <th className="px-2 py-1.5 text-right">{t('dialer.view.colAction')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i} className="border-t border-cream-100 hover:bg-cream-50">
                                    <td className="px-2 py-1.5">
                                        <span className={[
                                            'font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded',
                                            row.source === 'inbound' ? 'bg-success-100 text-success-500' : 'bg-warn-100 text-warn-500'
                                        ].join(' ')}>
                                            {row.source === 'inbound' ? t('dialer.view.badgeInbound') : t('dialer.view.badgeOutbound')}
                                        </span>
                                    </td>
                                    <td className="px-2 py-1.5 font-mono text-[11px] text-ink-700">{row.phone}</td>
                                    <td className="px-2 py-1.5 text-ink-900">{row.title || ''}{row.name}</td>
                                    <td className="px-2 py-1.5 text-ink-500 text-[11px]">
                                        {row.source === 'inbound'
                                            ? (row.address || row.dietary || row.preferredClinic || '-')
                                            : (row.category || '-')}
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        {row.source === 'inbound' && row.phone && (
                                            <button
                                                onClick={async () => {
                                                    if (!confirm(t('dialer.view.deleteMemberConfirm', { phone: row.phone }))) return;
                                                    await deleteMember(avatarId, row.phone);
                                                    reload();
                                                }}
                                                className="text-ink-400 hover:text-danger-500"
                                            >
                                                <Trash2 size={11} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
